<template>
  <div class="min-h-screen w-full">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
  const runtimeConfig = useRuntimeConfig();
  const { CONFIGURATION } = runtimeConfig.public;
  const { t } = useI18n();
  const route = useRoute();
  const mainStore = useMainStore();
  const menuStore = useMenuStore();
  const cookieYesStore = useCookieYesStore();
  const searchMenuStore = useSearchMenuStore();
  const { isAppLoading, isBodyFixed } = storeToRefs(mainStore);
  const { isMenuOpen } = storeToRefs(menuStore);
  const { isCookieYesConsent, isCookieYesCompleted } =
    storeToRefs(cookieYesStore);
  const { isMenuOpen: isSearchMenuOpen } = storeToRefs(searchMenuStore);
  const { handleScrollTo } = useScrollTo({ behavior: 'auto' });
  const { domain, captchaSiteKey, provideWebTitle } = useWebsite();
  const { scripts } = useMetaScript();
  const { canonical, alternate, preconnect, htmlLocaleName } = useMetaLink();
  const { isDesktop, isMobile } = useScreen();

  const cookieYesTimer = ref<NodeJS.Timeout | null>(null);

  /**
   * 百度追蹤碼整合
   * b20e405b5c9922d77e8360901e9dbd30 - 研華
   */

  const initBaiduAnalytics = (): void => {
    if (CONFIGURATION === 'ACN') {
      if (isCookieYesConsent.value && isCookieYesCompleted.value) {
        // 避免重複載入已存在的百度統計程式碼
        const isHmScriptExisted = document.querySelector(
          'script[src*="hm.baidu.com/hm.js"]'
        );
        if (isHmScriptExisted) {
          return;
        }

        console.log('偵測到 CookieYes 同意廣告追蹤，開始載入百度統計程式碼。');

        // eslint-disable-next-line no-var, @typescript-eslint/no-unused-vars
        var _hmt: any[] = window._hmt || [];
        (function () {
          const hm = document.createElement('script');
          hm.src =
            'https://hm.baidu.com/hm.js?b20e405b5c9922d77e8360901e9dbd30';
          const s = document.getElementsByTagName('script')[0];
          s?.parentNode?.insertBefore(hm, s);
        })();
        return;
      }
    }
  };

  // * Should be BodyFixed When Website Loading

  watch(
    isAppLoading,
    (value: boolean) => {
      mainStore.isBodyFixed = value;
    },
    { immediate: true }
  );

  // * Should be BodyFixed When Menu or SearchMenu Opening on Screen of Tablet & Mobile

  watch([isMenuOpen, isSearchMenuOpen, isDesktop], () => {
    if (isDesktop.value) {
      mainStore.isBodyFixed = false;
    } else {
      mainStore.isBodyFixed = isMenuOpen.value || isSearchMenuOpen.value;
    }
  });

  // * Should Close Menu When Change To Tablet or Desktop Screen from Mobile Screen

  watch(isMobile, (value: boolean) => {
    if (!value) {
      menuStore.HANDLE_CLOSE_MENU();
    }
  });

  // * BodyFixed Setting

  watch(
    isBodyFixed,
    (value: boolean) => {
      if (import.meta.client) {
        if (value) {
          document.body.classList.add('is-fixed');
        } else {
          document.body.classList.remove('is-fixed');
        }
      }
    },
    { immediate: true }
  );

  // * Watching Route Change

  watch(
    () => route.path,
    () => {
      // Scroll To Top
      handleScrollTo();
      // Close Menu
      menuStore.HANDLE_CLOSE_MENU();
      searchMenuStore.HANDLE_CLOSE_MENU();
    }
  );

  // * CookieYes Consent Advertisement Tracking

  watch([isCookieYesConsent, isCookieYesCompleted], () => {
    if (import.meta.client) {
      initBaiduAnalytics();
    }
  });

  onMounted(() => {
    cookieYesTimer.value = cookieYesStore.SET_COOKIE_YES_COMPLETE();
  });

  onBeforeUnmount(() => {
    if (cookieYesTimer.value) {
      clearTimeout(cookieYesTimer.value);
    }
  });

  useSeoMeta({
    title: () => t('meta.title'),
    ogTitle: () => t('meta.title'),
    twitterTitle: () => t('meta.title'),
    description: () => t('meta.description'),
    ogDescription: () => t('meta.description'),
    twitterDescription: () => t('meta.description'),
    ogLocale: htmlLocaleName.value,
    ogSiteName: CONFIGURATION === 'ACN' ? '研华工业云' : t('meta.title'),
    ogType: 'website',
    ogUrl: () => `${domain.value}${route.fullPath}`,
    ogImage: () =>
      CONFIGURATION === 'ACN'
        ? `${domain.value}/og-cn-image.png`
        : `${domain.value}/og-image.png`,
  });

  useHead({
    title: '',
    titleTemplate: (titleChunk) => provideWebTitle(titleChunk),
    htmlAttrs: {
      lang: () => htmlLocaleName.value,
      'data-captcha-client-key': captchaSiteKey.value,
    },
    bodyAttrs: {
      class: () => (isBodyFixed.value ? 'is-fixed' : ''),
    },
    link: () => [...preconnect.value, ...canonical.value, ...alternate.value],
    script: scripts.value,
  });

  useSchemaOrg([
    defineWebSite({
      name: t('meta.title'),
      description: t('meta.description'),
      inLanguage: htmlLocaleName.value,
      url: `${domain.value}`,
    }),
    defineWebPage({
      inLanguage: htmlLocaleName.value,
      url: `${domain.value}${route.fullPath}`,
    }),
  ]);
</script>
